import moment from 'moment-timezone';
import { OrderData, OrderStatus } from '../api';

export const PENDING_ORDER_STATUSES = [OrderStatus.PAYMENT_LINK_GENERATED, OrderStatus.PENDING_CONFIRMATION];

export const PENDING_ORDER_HATCH =
  'repeating-linear-gradient(45deg, rgba(255, 255, 255, 0.35) 0px, rgba(255, 255, 255, 0.35) 4px, transparent 4px, transparent 8px)';

export const PENDING_ORDER_HATCH_ON_LIGHT =
  'repeating-linear-gradient(45deg, rgba(0, 0, 0, 0.06) 0px, rgba(0, 0, 0, 0.06) 4px, transparent 4px, transparent 8px)';

export const isPendingOrder = (order: OrderData) => PENDING_ORDER_STATUSES.includes(order.status);

export const isPendingHoldExpired = (order: OrderData) =>
  isPendingOrder(order) && !!order.pending_expires_at && moment.utc(order.pending_expires_at).isBefore(moment.utc());

export type PendingHoldFields = { holdDate: string; holdTime: string };

// The hold editor works in the account's wall clock: 'YYYY-MM-DD' + 'HH:mm' fields, empty date
// meaning no limit. These helpers convert between those fields and the UTC instant the API speaks.
export const holdFieldsFromExpiry = (pendingExpiresAt: string | undefined, timezone: string): PendingHoldFields => {
  if (!pendingExpiresAt) {
    return { holdDate: '', holdTime: '' };
  }
  const localExpiry = moment.utc(pendingExpiresAt).tz(timezone);
  return { holdDate: localExpiry.format('YYYY-MM-DD'), holdTime: localExpiry.format('HH:mm') };
};

export const holdFieldsFromNow = (
  amount: number,
  unit: 'hour' | 'day' | 'week',
  timezone: string,
): PendingHoldFields => {
  const localExpiry = moment().tz(timezone).add(amount, unit);
  // The time picker works in 15-minute steps, so land the stamped deadline on that grid.
  const offGridMinutes = localExpiry.minute() % 15;
  if (offGridMinutes !== 0) {
    localExpiry.add(15 - offGridMinutes, 'minutes');
  }
  return { holdDate: localExpiry.format('YYYY-MM-DD'), holdTime: localExpiry.format('HH:mm') };
};

export const holdExpiryIso = (holdDate: string, holdTime: string, timezone: string): string | undefined =>
  holdDate
    ? moment
        .tz(`${holdDate} ${holdTime || '23:59'}`, 'YYYY-MM-DD HH:mm', timezone)
        .utc()
        .toISOString()
    : undefined;
